import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { flashcardService } from '../../services';

interface ImportCardsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (cards: Array<{ front: string; back: string; phonetic?: string; audio_url?: string }>) => void;
}

interface ParsedCard {
  front: string;
  back: string;
  phonetic?: string;
  audio_url?: string;
}

export const ImportCardsModal: React.FC<ImportCardsModalProps> = ({
  isOpen,
  onClose,
  onImport,
}) => {
  const [text, setText] = useState('');
  const [withPhonetics, setWithPhonetics] = useState(false);
  const [invalidLines, setInvalidLines] = useState<string[]>([]);
  const [isLoadingPhonetics, setIsLoadingPhonetics] = useState(false);

  useEffect(() => {
    const lines = text.split('\n').filter(line => line.trim() !== '');
    const invalid = lines.filter(line => !line.includes(':'));
    setInvalidLines(invalid);
  }, [text]);

  const parseCards = (): ParsedCard[] => {
    const cards: ParsedCard[] = [];
    const lines = text.split('\n').filter(line => line.trim() !== '');

    lines.forEach(line => {
      const parts = line.split(':').map(p => p.trim());
      
      if (parts.length >= 2) {
        cards.push({
          front: parts[0],
          back: parts.slice(1).join(':').trim(),
        });
      }
    });

    return cards;
  };

  const fetchPhonetics = async (cards: ParsedCard[]): Promise<ParsedCard[]> => {
    setIsLoadingPhonetics(true);
    try {
      const updatedCards = await Promise.all(
        cards.map(async (card) => {
          try {
            const result = await flashcardService.lookupWord(card.front, 'vi');

            console.log(`Response for "${card.front}":`, result);

            if (result?.phonetic) {
              console.log(`Found phonetic for "${card.front}": ${result.phonetic}`);
              return {
                ...card,
                phonetic: result.phonetic,
                audio_url: result.audioUrl || undefined,
              };
            }
            console.log(`No phonetic found for "${card.front}"`);
            return card;
          } catch (error) {
            console.error(`Error fetching phonetic for "${card.front}":`, error);
            return card;
          }
        })
      );
      return updatedCards;
    } catch (error) {
      console.error('Error fetching phonetics:', error);
      return cards;
    } finally {
      setIsLoadingPhonetics(false);
    }
  };

  const handleImport = async () => {
    if (invalidLines.length > 0) {
      alert('Vui lòng sửa các dòng không hợp lệ!');
      return;
    }

    let cards = parseCards();
    if (cards.length < 1) {
      alert('Vui lòng nhập ít nhất 1 từ!');
      return;
    }

    if (withPhonetics) {
      cards = await fetchPhonetics(cards);
    }

    onImport(cards);
    setText('');
    setWithPhonetics(false);
    onClose();
  };

  if (!isOpen) return null;

  const countNonEmptyLines = (text: string) => {
    return text.split('\n').filter(line => line.trim() !== '').length;
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Thêm bằng danh sách có sẵn</ModalTitle>
          <CloseButton onClick={onClose} disabled={isLoadingPhonetics}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <ModalDescription>
            Nhập danh sách từ, mỗi từ và nghĩa nằm trên 1 hàng, ngăn cách nhau bởi dấu hai chấm (:)
          </ModalDescription>

          <FormGroup>
            <TextArea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="VD: Hello:Xin chào&#10;World:Thế giới&#10;Book:Cuốn sách"
              rows={10}
              disabled={isLoadingPhonetics}
            />
            <LineCount>
              <strong>Tổng số từ: {countNonEmptyLines(text)}</strong>
            </LineCount>
          </FormGroup>

          <CheckboxGroup>
            <CheckboxInput
              type="checkbox"
              id="withPhonetics"
              checked={withPhonetics}
              onChange={() => setWithPhonetics(!withPhonetics)}
              disabled={isLoadingPhonetics}
            />
            <CheckboxLabel htmlFor="withPhonetics">
              Tự động lấy phiên âm từ từng từ
            </CheckboxLabel>
          </CheckboxGroup>

          {invalidLines.length > 0 && (
            <WarningBox>
              <WarningTitle>
                Các dòng sau không hợp lệ (Phải có dấu hai chấm ":"):
              </WarningTitle>
              <InvalidLinesList>
                {invalidLines.map((line, index) => (
                  <InvalidLine key={index}>{line}</InvalidLine>
                ))}
              </InvalidLinesList>
            </WarningBox>
          )}
        </ModalBody>

        <ModalFooter>
          <CancelButton onClick={onClose} disabled={isLoadingPhonetics}>
            Hủy
          </CancelButton>
          <ImportButton
            onClick={handleImport}
            disabled={invalidLines.length > 0 || countNonEmptyLines(text) === 0 || isLoadingPhonetics}
          >
            {isLoadingPhonetics ? 'Đang lấy phiên âm...' : `Thêm ${countNonEmptyLines(text)} từ`}
          </ImportButton>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-in;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(30px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 2px solid #e5e7eb;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    color: #1f2937;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
`;

const ModalDescription = styled.p`
  margin: 0 0 20px 0;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Courier New', monospace;
  resize: vertical;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const LineCount = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background: #f3f4f6;
  border-radius: 6px;
  font-size: 13px;
  color: #6b7280;
  text-align: center;

  strong {
    color: #3b82f6;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
`;

const CheckboxInput = styled.input`
  cursor: pointer;
  width: 18px;
  height: 18px;
  accent-color: #3b82f6;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CheckboxLabel = styled.label`
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  font-weight: 500;
  margin: 0;
`;

const WarningBox = styled.div`
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
`;

const WarningTitle = styled.p`
  margin: 0 0 8px 0;
  font-weight: 600;
  color: #92400e;
  font-size: 13px;
`;

const InvalidLinesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 150px;
  overflow-y: auto;
`;

const InvalidLine = styled.div`
  padding: 4px 8px;
  background: #fee2e2;
  border-left: 3px solid #ef4444;
  color: #7f1d1d;
  font-size: 12px;
  border-radius: 4px;
  word-break: break-word;
  font-family: 'Courier New', monospace;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0 0 16px 16px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    border-color: #9ca3af;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ImportButton = styled.button`
  padding: 10px 24px;
  border: none;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
